import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Any future live voice-call feature should dispatch this window event the
 * moment its WebSocket connects, so this hook can stop client-side speech
 * synthesis immediately and never overlap with a live AI audio stream.
 */
export const VOICE_CALL_STARTED_EVENT = "assistant:voice-call-started";

/**
 * useTextToSpeech — thin wrapper around the browser's native Web Speech API
 * (window.speechSynthesis) for reading assistant chat messages aloud.
 *
 * @param {string} language - BCP-47 language tag passed to SpeechSynthesisUtterance.
 * @returns {{
 *   isSupported: boolean,
 *   isPlaying: boolean,
 *   isPaused: boolean,
 *   currentMessageId: string|null,
 *   speak: (text: string, messageId: string) => void,
 *   pause: () => void,
 *   resume: () => void,
 *   stopAll: () => void,
 * }}
 */
export function useTextToSpeech(language) {
  const isSupported = typeof window !== "undefined" &&
    "speechSynthesis" in window && typeof window.SpeechSynthesisUtterance !== "undefined";

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState(null);
  const utteranceRef = useRef(null);

  const stopAll = useCallback(() => {
    if (isSupported) window.speechSynthesis.cancel();
    utteranceRef.current = null;
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentMessageId(null);
  }, [isSupported]);

  const speak = useCallback((text, messageId) => {
    if (!isSupported || !text) return;
    window.speechSynthesis.cancel(); // clear anything currently playing/paused first
    const utterance = new window.SpeechSynthesisUtterance(text);
    if (language) utterance.lang = language;
    utterance.onstart = () => { setIsPlaying(true); setIsPaused(false); setCurrentMessageId(messageId); };
    utterance.onend = () => { setIsPlaying(false); setIsPaused(false); setCurrentMessageId(null); utteranceRef.current = null; };
    utterance.onerror = () => { setIsPlaying(false); setIsPaused(false); setCurrentMessageId(null); utteranceRef.current = null; };
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [isSupported, language]);

  const pause = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.pause();
    setIsPlaying(false);
    setIsPaused(true);
  }, [isSupported]);

  const resume = useCallback(() => {
    if (!isSupported) return;
    window.speechSynthesis.resume();
    setIsPlaying(true);
    setIsPaused(false);
  }, [isSupported]);

  // Voice-call conflict handling.
  useEffect(() => {
    if (!isSupported) return;
    window.addEventListener(VOICE_CALL_STARTED_EVENT, stopAll);
    return () => window.removeEventListener(VOICE_CALL_STARTED_EVENT, stopAll);
  }, [isSupported, stopAll]);

  // Stop speaking if whatever owns this hook unmounts mid-utterance.
  useEffect(() => () => { if (isSupported) window.speechSynthesis.cancel(); }, [isSupported]);

  return { isSupported, isPlaying, isPaused, currentMessageId, speak, pause, resume, stopAll };
}
